import { isValid, parseISO } from "date-fns";

import { POPUP_GROUP_DISTRIBUTION_LABELS } from "../PopupGroupForm/popupGroupDistributionLabels";

import {
  normalizePopupImportDisplayPopupAgain,
  popupImportFormActionKindRawFromCsv,
  popupImportFormAnimationKindRawFromCsv,
  popupImportFormClosePopupByClickingOutsideRawFromCsv,
  popupImportFormColorKindRawFromCsv,
  popupImportFormContentKindRawFromCsv,
  popupImportFormDisplayKindRawFromCsv,
  popupImportFormHrefTargetRawFromCsv,
  popupImportFormYdaTriggerRawFromCsv,
} from "./convertFunctions";
import {
  POPUP_IMPORT_ACTION_HEADER,
  POPUP_IMPORT_CSV_HEADERS,
} from "./popupImportCsvHeaders";
import {
  POPUP_IMPORT_ALLOWED_AGE_RANGE,
  POPUP_IMPORT_ALLOWED_ANNUAL_INCOME,
  POPUP_IMPORT_ALLOWED_BROWSER,
  POPUP_IMPORT_ALLOWED_DEVICE,
  POPUP_IMPORT_ALLOWED_GENDER,
  POPUP_IMPORT_ALLOWED_PLATFORM,
  normalizeSegmentEnumImportCell,
} from "./popupImportGraphqlEnumCells";
import type {
  PopupImportDeliverDayOfWeekRow,
  PopupImportRowValues,
} from "./validationSchema";

const trim = (v: unknown): string =>
  typeof v === "string" ? v.trim() : String(v ?? "").trim();

/** Trim + strip UTF-8 BOM and one layer of CSV-style wrapping quotes. */
const normalizeCell = (raw: string): string => {
  let t = raw.replace(/^\uFEFF/, "").trim();
  if (
    (t.startsWith('"') && t.endsWith('"')) ||
    (t.startsWith("'") && t.endsWith("'"))
  ) {
    t = t.slice(1, -1).trim();
  }
  return t;
};

/** Repeatedly strip matching `"` / `'` wrappers (e.g. Excel-style `""id""`). */
export function stripSurroundingCsvQuotes(raw: string): string {
  let t = raw.replace(/^\uFEFF/, "").trim();
  let prev = "\u0000";
  while (t !== prev) {
    prev = t;
    if (
      (t.startsWith('"') && t.endsWith('"')) ||
      (t.startsWith("'") && t.endsWith("'"))
    ) {
      t = t.slice(1, -1).trim();
    }
  }
  return t;
}

const cellId = (r: Record<string, unknown>, key: string): string =>
  stripSurroundingCsvQuotes(trim(r[key] ?? ""));

export const splitListCell = (raw: string): string[] => {
  const t = trim(raw);
  if (!t) return [];
  return t
    .split(/[;|]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const parseBool = (raw: string): boolean => {
  const t = trim(raw).toLowerCase();
  return t === "true" || t === "1" || t === "yes" || t === "t";
};

const cell = (r: Record<string, unknown>, key: string): string =>
  normalizeCell(trim(r[key] ?? ""));

const parseJsonArray = <T>(raw: string): T[] | null => {
  const t = trim(raw);
  if (!t) return null;
  try {
    const v = JSON.parse(t) as unknown;
    return Array.isArray(v) ? (v as T[]) : null;
  } catch {
    return null;
  }
};

export const referrersFromCell = (raw: string) => {
  const json = parseJsonArray<{ referrer: string; matchOp: string }>(raw);
  if (json)
    return json.map((x) => ({
      referrer: trim(x.referrer),
      matchOp: trim(x.matchOp) || "EQUAL",
    }));
  const parts = splitListCell(raw);
  return parts.map((referrer) => ({ referrer, matchOp: "EQUAL" }));
};

export const excludeReferrersFromCell = (raw: string) => {
  const json = parseJsonArray<{ referrer: string; matchOp: string }>(raw);
  if (json)
    return json.map((x) => ({
      referrer: trim(x.referrer),
      matchOp: trim(x.matchOp) || "EQUAL",
    }));
  const parts = splitListCell(raw);
  return parts.map((referrer) => ({ referrer, matchOp: "EQUAL" as const }));
};

export const urlsFromCell = (raw: string) =>
  splitListCell(raw).map((url) => ({ url }));

export const targetContentsFromCell = (raw: string) => {
  const json = parseJsonArray<{ targetKind: string; targetValue: string }>(raw);
  if (json)
    return json.map((x) => ({
      targetKind: trim(x.targetKind) || "SELECTOR",
      targetValue: trim(x.targetValue),
    }));
  const parts = splitListCell(raw);
  return parts.map((targetValue) => ({
    targetKind: /^[.#]/.test(targetValue) ? "SELECTOR" : "ID",
    targetValue,
  }));
};

export const disableTargetsFromCell = (raw: string) => {
  const json = parseJsonArray<{ targetKind: string; targetValue: string }>(raw);
  if (json)
    return json.map((x) => ({
      targetKind: trim(x.targetKind) || "SELECTOR",
      targetValue: trim(x.targetValue),
    }));
  const parts = splitListCell(raw);
  return parts.map((targetValue) => ({
    targetKind: /^[.#]/.test(targetValue) ? "SELECTOR" : "ID",
    targetValue,
  }));
};

function cellToDate(raw: unknown): Date | null {
  const t = trim(raw);
  if (!t) return null;
  const parsed = parseISO(t);
  if (isValid(parsed)) return parsed;
  const d = new Date(t);
  return isValid(d) ? d : null;
}

function scheduleIdsFromCell(raw: unknown): string[] {
  return splitListCell(trim(raw));
}

const DAY_OF_WEEK_INDEX_TO_ENUM: Record<string, string> = {
  "0": "SUN",
  "1": "MON",
  "2": "TUE",
  "3": "WED",
  "4": "THU",
  "5": "FRI",
  "6": "SUT",
};

function normalizeDayOfWeekValue(raw: string): string {
  const t = raw.trim();
  const mapped = DAY_OF_WEEK_INDEX_TO_ENUM[t];
  if (mapped) return mapped;
  return t.toUpperCase();
}

function deliverDayOfWeeksFromCell(
  raw: unknown
): PopupImportDeliverDayOfWeekRow[] {
  const t = trim(raw);
  if (!t) return [];
  try {
    const v = JSON.parse(t) as unknown;
    if (!Array.isArray(v)) return [];
    return v
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const o = item as Record<string, unknown>;
        const weekRaw = o.weekOfMonth;
        const wm =
          weekRaw === null || weekRaw === undefined || weekRaw === ""
            ? null
            : Number(weekRaw);
        const weekOfMonth = wm !== null && Number.isFinite(wm) ? wm : null;
        const daysRaw = o.daysOfWeek;
        const daysOfWeek = Array.isArray(daysRaw)
          ? daysRaw
              .map((x) => normalizeDayOfWeekValue(String(x)))
              .filter(Boolean)
          : [];
        return { weekOfMonth, daysOfWeek };
      })
      .filter((x): x is PopupImportDeliverDayOfWeekRow => x != null);
  } catch {
    return [];
  }
}

function distributionTypeToIsMulti(raw: unknown): string {
  const t = trim(raw);
  if (!t) return "false";
  const lower = t.toLowerCase();
  if (lower === "true" || lower === "1" || t.includes("マルチ")) return "true";
  return "false";
}

function deliverKindFromCell(raw: unknown): string {
  const t = trim(String(raw ?? ""));
  if (!t) return "EQUAL";
  const upper = t.toUpperCase();
  if (upper === "WEIGHT") return "WEIGHT";
  if (upper === "EQUAL") return "EQUAL";
  if (t.includes("配信比率")) return "WEIGHT";
  return "EQUAL";
}

export const cookiesFromCell = (raw: string) => {
  const json = parseJsonArray<{
    key: string;
    value: string;
    matchType: boolean;
  }>(raw);
  if (json)
    return json.map((x) => ({
      key: trim(x.key),
      value: trim(x.value),
      matchType: Boolean(x.matchType),
    }));
  const t = trim(raw);
  if (!t) return [];
  return t
    .split(";")
    .map((part) => {
      const p = part.trim();
      if (!p) return null;
      const neq = p.indexOf("!=");
      if (neq !== -1) {
        return {
          key: p.slice(0, neq).trim(),
          value: p.slice(neq + 2).trim(),
          matchType: false,
        };
      }
      const eq = p.indexOf("=");
      if (eq !== -1) {
        return {
          key: p.slice(0, eq).trim(),
          value: p.slice(eq + 1).trim(),
          matchType: true,
        };
      }
      return { key: p, value: "", matchType: true };
    })
    .filter(
      (x): x is { key: string; value: string; matchType: boolean } => x !== null
    );
};

const imageBlock = (r: Record<string, unknown>, n: number) => {
  const url = cell(r, `画像${n}URL`);
  const honeycombCode = cell(r, `画像${n}YDAコード`);
  const alt = cell(r, `画像${n}画像が表示されない場合の表記`);
  const linkUrl = cell(r, `画像${n}画像クリック時のURL`);
  const inheritRaw = cell(
    r,
    `画像${n}クエリパラメータを引き継ぐ（※ページの?以降の文字を引き継ぎます）`
  );
  if (!url && !honeycombCode && !alt && !linkUrl && !inheritRaw) return null;
  return {
    imageUrl: url,
    honeycombCode,
    alt,
    linkUrl,
    isInheritQuery: parseBool(inheritRaw),
  };
};

/** Highest 画像N index (1-based) with any image-block field set; 0 if none. */
export function maxImageSlotIndexInRecord(r: Record<string, unknown>): number {
  let max = 0;
  for (let n = 1; n <= 8; n++) {
    if (imageBlock(r, n)) max = n;
  }
  return max;
}

/** Empty/missing column → `""`; invalid non-empty → `null` (INVALID_ACTION). */
const parseAction = (raw: string): "CREATE" | "UPDATE" | "" | null => {
  const t = trim(raw).toUpperCase();
  if (!t) return "";
  if (t === "CREATE" || t === "新規作成" || t === "登録") return "CREATE";
  if (t === "UPDATE" || t === "更新" || t === "編集") return "UPDATE";
  return null;
};

export function recordToPopupImportRow(
  r: Record<string, unknown>
): Omit<PopupImportRowValues, "_clientRowId"> {
  const popupId = cellId(r, "ポップアップID");
  const popupGroupId = cellId(r, "ポップアップグループID");
  const actionRaw = cell(r, POPUP_IMPORT_ACTION_HEADER);
  let action = parseAction(actionRaw);
  if (action === null) throw new Error("INVALID_ACTION");
  if (action === "") {
    action = popupId !== "" ? "UPDATE" : "CREATE";
  }

  const images = [];
  for (let i = 1; i <= 8; i++) {
    const b = imageBlock(r, i);
    if (b) images.push(b);
  }

  return {
    action,
    popupId,
    popupGroupId,
    popupGroupName: cell(r, "ポップアップグループ名"),
    verificationFunnelId: cell(r, "ファネルID"),
    verificationPageId: "",
    verificationPageUrl: cell(r, "実施するページURL"),
    group: {
      name: cell(r, "ポップアップグループ名"),
      memo: cell(r, "メモ"),
      startDate: cellToDate(cell(r, "実施開始日")),
      endDate: cellToDate(cell(r, "実施終了日")),
      scheduleIds: scheduleIdsFromCell(cell(r, "実施時刻")),
      deliverDayOfWeeks: deliverDayOfWeeksFromCell(cell(r, "実施曜日")),
      redisplayPopupTime: cell(r, "再表示禁止期間"),
      devices: normalizeSegmentEnumImportCell(
        cell(r, "デバイス"),
        POPUP_IMPORT_ALLOWED_DEVICE
      ),
      platforms: normalizeSegmentEnumImportCell(
        cell(r, "プラットフォーム"),
        POPUP_IMPORT_ALLOWED_PLATFORM
      ),
      browsers: normalizeSegmentEnumImportCell(
        cell(r, "ブラウザ"),
        POPUP_IMPORT_ALLOWED_BROWSER
      ),
      genders: normalizeSegmentEnumImportCell(
        cell(r, "性別"),
        POPUP_IMPORT_ALLOWED_GENDER
      ),
      ageRanges: normalizeSegmentEnumImportCell(
        cell(r, "年齢"),
        POPUP_IMPORT_ALLOWED_AGE_RANGE
      ),
      annualIncomes: normalizeSegmentEnumImportCell(
        cell(r, "年収"),
        POPUP_IMPORT_ALLOWED_ANNUAL_INCOME
      ),
      sourceIds: splitListCell(cell(r, "表示条件に流入元カテゴリを追加する")),
      excludeSourceIds: splitListCell(
        cell(r, "非表示条件に流入元カテゴリを追加する")
      ),
      referrers: referrersFromCell(cell(r, "表示条件に流入元を追加する")),
      excludeReferrers: excludeReferrersFromCell(
        cell(r, "表示条件に除外流入元を追加する")
      ),
      visitCount: cell(r, "表示条件にユーザーの訪問回数を追加"),
      displayTargetContents: targetContentsFromCell(
        cell(r, POPUP_GROUP_DISTRIBUTION_LABELS.displayTargetContents)
      ),
      disableTargetElements: disableTargetsFromCell(
        cell(r, POPUP_GROUP_DISTRIBUTION_LABELS.disableTargetElements)
      ),
      deliverTargetUrls: urlsFromCell(
        cell(r, POPUP_GROUP_DISTRIBUTION_LABELS.deliverTargetUrls)
      ),
      deliverExcludeUrls: urlsFromCell(
        cell(r, POPUP_GROUP_DISTRIBUTION_LABELS.deliverExcludeUrls)
      ),
      targetCookies: cookiesFromCell(
        cell(r, POPUP_GROUP_DISTRIBUTION_LABELS.targetCookies)
      ),
      isMulti: distributionTypeToIsMulti(cell(r, "マルチ配信")),
      deliverKind: deliverKindFromCell(cell(r, "配信方法")),
      weightRaw: cell(r, "配信比率"),
      contentKindRaw: popupImportFormContentKindRawFromCsv(
        cell(r, "ポップアップの種類")
      ),
    },
    popup: {
      name: cell(r, "ポップアップ名"),
      isClose: parseBool(cell(r, "閉じるボタンの表示")),
      contentWidth: cell(r, "ポップアップの幅"),
      spContentWidth: cell(r, "スマートフォンポップアップの幅"),
      colorKindRaw: popupImportFormColorKindRawFromCsv(cell(r, "配色デザイン")),
      title: cell(r, "タイトル"),
      description: cell(r, "説明文"),
      button: cell(r, "ボタンテキスト"),
      url: cell(r, "クリック時のURL"),
      isInheritQuery: cell(
        r,
        "クエリパラメータを引き継ぐ（※ページの?以降の文字を引き継ぎます）"
      ),
      hrefTargetRaw: popupImportFormHrefTargetRawFromCsv(
        cell(r, "クリック時のタブの扱い")
      ),
      actionKindRaw: popupImportFormActionKindRawFromCsv(
        cell(r, "表示タイミング")
      ),
      displayOnTabFocus: cell(r, "タブの切り替えで表示"),
      displayOnMouseOut: cell(r, "ポインタが外れた際にポップアップを表示"),
      isDisplayWhenNoOperation: cell(
        r,
        "15秒ユーザー操作がない場合ポップアップを表示"
      ),
      customEvent: cell(r, "（コード）"),
      displayKindRaw: popupImportFormDisplayKindRawFromCsv(cell(r, "表示場所")),
      enableTapWindow: cell(r, "背景の操作"),
      closePopupByClickingOutsideRaw:
        popupImportFormClosePopupByClickingOutsideRawFromCsv(
          cell(r, "背景をクリック時にポップアップを非表示にする")
        ),
      backgroundOpacity: cell(r, "背景濃度"),
      hideByOnfocus: cell(r, "入力中にポップアップを表示させない"),
      quitTiming: cell(r, "表示時間"),
      displayCountLimit: cell(r, "表示回数を制限"),
      displayPopupAgain: normalizePopupImportDisplayPopupAgain(
        cell(r, "再表示ボタン")
      ),
      displayPopupAgainImage: {
        imageUrl: cell(r, "再表示ボタン用画像"),
      },
      displayPopupAgainMessage: cell(r, "再表示ボタン用メッセージ"),
      animationKindRaw: popupImportFormAnimationKindRawFromCsv(
        cell(r, "アニメーション")
      ),
      ydaTriggerRaw: popupImportFormYdaTriggerRawFromCsv(
        cell(r, "YDAコードトリガー")
      ),
      honeycombCode: cell(r, "YDAコード"),
      statusRaw: cell(r, "ステータス"),
      hideByScrollPercentageRaw: cell(r, "スクロール率制限"),
      scrollRateRangeRaw: "",
    },
    popupImages: images,
    // carry funnel name for display only
    funnelName: cell(r, "ファネル名"),
  };
}

export function validateHeaderFields(fields: string[]): string | null {
  const hasAction = fields[0] === POPUP_IMPORT_ACTION_HEADER;
  const expected = hasAction
    ? [POPUP_IMPORT_ACTION_HEADER, ...POPUP_IMPORT_CSV_HEADERS]
    : [...POPUP_IMPORT_CSV_HEADERS];
  if (fields.length < expected.length) {
    return `CSVの列数が不足しています（期待: ${expected.length}列、実際: ${fields.length}列）`;
  }
  for (let i = 0; i < expected.length; i++) {
    if (fields[i] !== expected[i]) {
      return `CSVの${i + 1}列目のヘッダーが一致しません（期待: ${
        expected[i]
      }、実際: ${fields[i] ?? ""}）`;
    }
  }
  return null;
}
